// Catalogos y maestro de productos que alimentan los desplegables del formulario.
//
// HU-02 y HU-30: las listas son CONFIGURABLES, no constantes del front. "El
// administrador puede agregar el valor sin que se redespliegue el sistema" — si
// estuvieran aqui en duro, agregar una profesion exigiria recompilar la SPA.

import { pedir, seg, segPais } from './cliente';
import type { RespuestaDelApi } from '../tipos';

/**
 * Los catalogos MANTENIBLES que el formulario del Sprint 1 necesita.
 *
 * `categoria_ea` NO esta aqui, y no es un olvido: el backend lo excluye de
 * `NombreDeCatalogo` a proposito —su columna `activa_dato_l2` es RN-09 y solo cambia
 * con un cambio de esquema— asi que `/referencias/catalogos/categoria_ea` responde
 * error. Las categorias de la seccion 3 tienen ruta propia de solo lectura:
 * `listarCategoriasDeEvento()`, mas abajo.
 */
export const CATALOGO = {
  PROFESION_O_ROL: 'profesion_o_rol',
  ESPECIE: 'especie',
  ESTADO_FISIOLOGICO: 'estado_fisiologico',
  VIA_EXPOSICION_PERSONA: 'via_exposicion_persona',
  SITIO_INYECCION: 'sitio_inyeccion',
  // La escala de desenlace de la seccion 6. Estuvo escrita a mano en la pantalla —y a
  // la vez en un CHECK de la base y en un enum del backend— hasta el 25-ago-2026.
  // Ahora es tabla (`desenlace_evento`) y se amplia desde la pantalla de HU-30.
  DESENLACE_EVENTO: 'desenlace_evento',
  // Las respuestas a "Te parece grave?" — la OPINION del registrador. `caso.gravedad`,
  // que decide el Comite, NO es catalogo y no debe serlo: de ella cuelgan el plazo ante
  // la autoridad y la ruta regulatoria (RN-03).
  CRITERIO_PRELIMINAR: 'criterio_preliminar',
};

// `/referencias` y no `/administracion`: este ultimo exige rol `administrador` y
// quien llena el formulario es el registrador. Son las MISMAS listas, servidas por
// un router de solo lectura con otra guarda.
/** La respuesta de un catalogo: NO es una lista suelta, viene envuelta en `valores`. */
export type RespuestaDeCatalogo = {
  catalogo: string;
  valores: { codigo: string; nombre?: string; activo?: boolean }[];
};

/** Una categoria de evento adverso. `activa_dato_l2` es RN-09 y la decide la base. */
export type CategoriaDeEvento = {
  codigo: string;
  nombre: string;
  activo?: boolean;
  activa_dato_l2?: boolean;
};

export function listarCatalogo(catalogo: string) {
  return pedir<RespuestaDeCatalogo>(`/referencias/catalogos/${seg(catalogo)}`);
}

/**
 * HU-03 y HU-06. Las categorias de la seccion 3 ("Identificacion del EA").
 *
 * Devuelve `activa_dato_l2` por categoria: es RN-09 y viaja desde la base para que la
 * regla NO quede escrita tambien en el front, donde se queda atras. Marcar la
 * categoria que la trae encendida es lo que habilita el bloque de datos L2.
 */
export function listarCategoriasDeEvento() {
  return pedir<CategoriaDeEvento[]>('/referencias/categorias-de-evento');
}

/**
 * HU-01. Los 14 paises del alcance, con la autoridad que los regula.
 *
 * ESTABAN EN DURO EN EL FRONT (`src/data/paises.js`) y la base ya tenia las dos
 * tablas —`pais` y `parametro_pais`— sin que nadie las leyera: corregir el nombre de
 * un pais o su autoridad en la BD no cambiaba nada en pantalla. Dos copias de un dato
 * que decide autoridad, plazos y quien firma (RN-06), sin nada que las mantuviera
 * iguales.
 *
 * Ruta propia de solo lectura y NO `/catalogos/pais`: aquella sirve catalogos
 * MANTENIBLES y comparte comprobacion con sus escrituras, asi que incluir `pais` ahi
 * habilitaria darlo de baja por pantalla — y un pais inactivo deja correlativos
 * (`PE-001-2026`) apuntando a la nada.
 *
 * Devuelve `exige_profesional_responsable` por pais (RN-05, quien firma). Hoy el
 * formulario no lo usa; viaja para que el dia que lo use no vuelva a escribirse aqui.
 */
export function listarPaises() {
  return pedir<Record<string, unknown>[]>('/referencias/paises');
}

/**
 * HU-31. Los topes de cada casilla y las reglas del correo.
 *
 * NO son catalogos —no dicen que opciones existen, dicen cuanto cabe— pero se piden
 * por el mismo router y al mismo tiempo, porque la pantalla los necesita juntos para
 * pintarse. Estaban escritos en el JSX (`maxLength={11}`) y ampliar un campo obligaba
 * a redesplegar la SPA.
 */
export function configuracionDelFormulario() {
  return pedir<RespuestaDelApi>('/referencias/configuracion-del-formulario');
}

/** Todos de una vez: el formulario los necesita juntos al abrirse. */
export async function catalogosDelFormulario() {
  const nombres = Object.values(CATALOGO);
  // El `catch` devuelve la MISMA forma que el exito -`{catalogo, valores}`- y no una
  // lista suelta: si un catalogo falla, quien lo consume no deberia tener que
  // distinguir entre dos formas distintas del mismo dato.
  const listas = await Promise.all(
    nombres.map((n) => listarCatalogo(n).catch((): RespuestaDeCatalogo => ({ catalogo: n, valores: [] }))),
  );
  return Object.fromEntries(nombres.map((n, i) => [n, listas[i]])) as Record<string, RespuestaDeCatalogo>;
}

/** Una opcion del buscador (`ProductoDelMaestroSalida`): la ficha del producto EN EL PAIS del caso. */
export type ProductoDelMaestro = {
  sku: string;
  nombre_comercial: string;
  forma_farmaceutica: string | null;
  concentracion: string | null;
  registro_sanitario: string | null;
  /** HU-29-C9: el pais no tiene ninguna fila cargada y se ofrecen las semillas globales. */
  es_dato_de_prueba: boolean;
  /**
   * CC-02 (CC2-04-C1): los registros vigentes por autoridad, la predeterminada primero. Vacío en
   * las semillas; ausente en un servidor anterior a CC-02.
   */
  registros?: RegistroDelProducto[];
};

/** CC-02 (`RegistroDelProductoSalida`): el registro del producto ante una autoridad del país. */
export type RegistroDelProducto = {
  autoridad_codigo: string;
  autoridad_nombre: string;
  registro_sanitario: string;
};

/** `MaestroDelPaisSalida` (endpoint 46). `texto_vacio` (HU-29-C8) solo cuando el pais no ofrece nada. */
export type MaestroDelPais = {
  pais: string;
  productos: ProductoDelMaestro[];
  texto_vacio: string | null;
};

/**
 * HU-04 y HU-29 (tarea 29-6). El maestro que alimenta el buscador de productos, DEL PAIS DEL
 * CASO: el mismo SKU tiene otro registro sanitario en Chile que en Peru (HU-29-C12), y el
 * buscador ofrece la ficha del pais del borrador (AT S3 §2.2.6, endpoint 46).
 *
 * El servidor exige `?pais=` y responde un OBJETO `{pais, productos, texto_vacio}`: la version
 * anterior pedia el maestro entero sin pais y lo leia como lista, y el registro se quedaba con
 * «El maestro de productos no esta disponible». No devuelve los productos retirados.
 */
export function listarMaestroDeSku(pais: string) {
  return pedir<MaestroDelPais>(`/referencias/maestro-de-productos?pais=${segPais(pais)}`);
}

// ---------------------------------------------------------------------------------
// HU-30 (MG-ILE-58) · tarea 30-4. El MANTENIMIENTO de los catálogos (Administración › Catálogos).
//
// Por `/administracion` (rol administrador), no por `/referencias`. Nombres del contrato:
// `IndiceDeCatalogosSalida`, `CatalogoSalida`, `ValorDeCatalogoSalida` (esquemas.py). La etiqueta
// de cada catálogo, el texto del estado de cada valor y las acciones las entrega el servidor.
// ---------------------------------------------------------------------------------

export type EntradaDelIndice = { catalogo: string; etiqueta: string; valores_activos: number; valores_retirados: number };

export type IndiceDeCatalogos = {
  catalogos: EntradaDelIndice[];
  /** HU-30-C8: País, Categoría del evento y Rol del sistema, con dónde se mantiene cada uno. */
  no_mantenidos_aqui: { nombre: string; donde_se_mantiene: string }[];
};

export type ValorDelCatalogo = {
  codigo: string;
  nombre: string;
  orden: number;
  activo: boolean;
  /** «Activo» / «Retirado». */
  texto_del_estado: string;
  /** `editar` y `retirar`, o `reactivar`. */
  acciones_permitidas: string[];
};

export type CatalogoParaAdministrar = {
  catalogo: string;
  etiqueta: string;
  valores: ValorDelCatalogo[];
  /** `agregar`. */
  acciones_permitidas: string[];
};

const ADMIN = '/administracion/catalogos';

export function indiceDeCatalogos() {
  return pedir<IndiceDeCatalogos>(ADMIN);
}

/** Con los retirados: la pantalla los necesita para poder reactivarlos (HU-30-C5). */
export function catalogoParaAdministrar(catalogo: string) {
  return pedir<CatalogoParaAdministrar>(`${ADMIN}/${seg(catalogo)}?incluir_inactivos=true`);
}

/** HU-30-C1, C2. El contrato vigente exige `codigo` (vacío V-07 de la spec). 409 si la etiqueta ya existe. */
export function agregarValor(catalogo: string, valor: { codigo: string; nombre: string; orden: number }) {
  return pedir<ValorDelCatalogo>(`${ADMIN}/${seg(catalogo)}`, { metodo: 'POST', cuerpo: valor });
}

/** Editar (C2, C3), retirar (`activo: false`) o reactivar (`activo: true`, C5). No hay DELETE (RN-53). */
export function editarValor(catalogo: string, codigo: string, cambios: { nombre?: string; orden?: number; activo?: boolean }) {
  return pedir<ValorDelCatalogo>(`${ADMIN}/${seg(catalogo)}/${seg(codigo)}`, { metodo: 'PATCH', cuerpo: cambios });
}
